"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.deleteMatchingClauses = exports.deleteClause = exports.addGroupClause = exports.getClauseBranch = exports.reduceClauseBranches = exports.getFilteredClauseTree = exports.addClause = exports.getFlattenedClausesFromNestedConditions = exports.getNestedConditionsFromFlattenedClauses = undefined;

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _constants = require("./constants");

var _utils = require("./utils");

var _filters = require("./filters");

var _index = require("../general/index");

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Takes in a list of clauses, returns a nested structure that the API/Firebase likes.  Allows mixing of conditionNames.
 *
 * For example...
 *
 * [
 *  { id: 1, operator: "boolean", value: "or" },
 *  { id: "1-1", parentId: 1, conditionName: "url", operator: "regex", value: "\/\w+\/foo" }
 *  { id: "1-2", parentId: 1, conditionName: "properties", property: "language" operator: "==", value: "petelish" }
 * ]
 *
 * ...becomes...
 *
 * {
 *  "or": [
 *    { url: { operator: "regex", value: "\/\w+\/foo" } },
 *    { properties: { property: "language", operator: "==", value: "petelish" } }
 *  ]
 * }
 *
 * @param clauses
 * @param masterClause
 * @returns {*}
 */

var getNestedConditionsFromFlattenedClauses = exports.getNestedConditionsFromFlattenedClauses = function getNestedConditionsFromFlattenedClauses(clauses, masterClause, validKeys) {
  var validClauses = clauses.filter(Boolean);

  if (validClauses && !masterClause) {
    masterClause = (0, _utils.getMasterClause)(validClauses);
  }

  if (masterClause) {
    if (masterClause.operator === _constants.groupOperators.BOOLEAN) {
      var childClauses = validClauses.filter(function (it) {
        return it.parentId === masterClause.id;
      });

      return _defineProperty({}, masterClause.value, childClauses.map(function (cc) {
        return getNestedConditionsFromFlattenedClauses(validClauses, cc, validKeys);
      }));
    } else {
      var nestedClause = _defineProperty({}, masterClause.conditionName, (0, _index.whitelist)(masterClause, validKeys));

      if (masterClause.negative) {
        return { not: [nestedClause] };
      } else {
        return nestedClause;
      }
    }
  } else {
    return {};
  }
};

/**
 * Takes in an API/Firebase style condition (ex. `url` or `domains`) and flattens
 * it into clauses that can be used by Redux.  Note that random ID's are currently assigned
 * during this process and they do not matter
 *
 * {
 *  or: [
 *      { url: { operator: "equals", "foo" }},
 *      { and: [
 *          { url: { operator: "equals", value: "bar" }},
 *          { domains: { operator: "equals", value: "baz" }}
 *        ]
 *      }
 *  ]
 * }
 *
 * ...becomes...
 *
 * [
 *  { id: "1", operator: "boolean", value: "or" },
 *  { id: "1-1", parentId: 1, conditionName: "url", operator: "equals", value: "foo" },
 *  { id: "1-2", parentId: 1, operator: "boolean", value: "and" }
 *  { id: "1-2-1-1", parentId: "1-2", conditionName: "url", operator: "equals", value: "bar" },
 *  { id: "1-2-2-1", parentId: "1-2", conditionName: "domains", operator: "equals", value: "baz" }
 * ]
 *
 * @param nestedCondition
 * @param parentId
 * @param index
 * @param conditionName
 * @param negative
 * @returns {*}
 */

var getFlattenedClausesFromNestedConditions = exports.getFlattenedClausesFromNestedConditions = function getFlattenedClausesFromNestedConditions() {
  var nestedCondition = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var parentId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var index = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var conditionName = arguments[3];
  var negative = arguments[4];

  var primaryKey = Object.keys(nestedCondition)[0];

  // have to map because `Object.values()` isn't introduced until ES2017!
  var groupOperatorValueList = Object.keys(_constants.groupOperatorValues).map(function (it) {
    return _constants.groupOperatorValues[it];
  });
  var conditionNameList = Object.keys(_constants.conditionNames).map(function (it) {
    return _constants.conditionNames[it];
  });

  if (!primaryKey || !(primaryKey in nestedCondition)) {
    return [];
  } else if (groupOperatorValueList.includes(primaryKey)) {
    var operatorClauseId = parentId ? parentId + "-" + index : "" + index;

    var initialState = [{
      id: operatorClauseId,
      parentId: parentId,
      operator: _constants.groupOperators.BOOLEAN,
      value: primaryKey
    }];

    return nestedCondition[primaryKey].filter(Boolean).reduce(function (prev, clause, c) {
      return [].concat(_toConsumableArray(prev), _toConsumableArray(getFlattenedClausesFromNestedConditions(clause, operatorClauseId, c + 1, conditionName, negative)));
    }, initialState);
  } else if (conditionNameList.includes(primaryKey) && nestedCondition[primaryKey] instanceof Object) {
    var _initialState = [];
    var clauseId = parentId;

    if (Object.keys(nestedCondition).length > 1) {
      /*
             * We've discovered a level with multiple primary keys in the same object.
             * This is the implicit `and` case, so we must handle it by making the `and` EXplicit
             */

      clauseId = parentId ? parentId + "-" + index : "" + index;

      _initialState = [{
        id: "" + clauseId,
        parentId: parentId,
        operator: _constants.groupOperators.BOOLEAN,
        value: _constants.groupOperatorValues.AND
      }];
    }

    /**
     * `${index}-${c + 1}` below only exists because we don't require an implicit `and` clause when coming
     * across { conditionName: { clause }}.  conditionName must, however, be considered its own level
     * to ensure that assigned id's are unique.
     *
     * might be purer to someday add an implicit `add` regardless of the number of keys seen, but this works.
     */

    return Object.keys(nestedCondition).filter(Boolean).reduce(function (prev, cName, c) {
      return [].concat(_toConsumableArray(prev), _toConsumableArray(getFlattenedClausesFromNestedConditions(nestedCondition[cName], clauseId, index + "-" + (c + 1), cName, negative)));
    }, _initialState);
  } else if (primaryKey === "not") {
    return nestedCondition[primaryKey].filter(Boolean).reduce(function (prev, clause, c) {
      return [].concat(_toConsumableArray(prev), _toConsumableArray(getFlattenedClausesFromNestedConditions(clause, parentId, index + "-" + (c + 1), conditionName, true)));
    }, []);
  } else {
    var negation = negative ? { negative: true } : {};

    return [_extends({}, nestedCondition, negation, {
      id: parentId ? parentId + "-" + index : index,
      parentId: parentId,
      conditionName: conditionName
    })];
  }
};

var addClause = exports.addClause = function addClause(clauses, parentId, clause) {
  var groupByConditionName = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;

  if (clauses.length === 0) {
    /**
     * no clauses exist for this step yet, so first make sure a root `and` clause is present
     */

    var groupClauseReduxId = (0, _utils.getClauseReduxId)(clauses, null);

    clauses = clauses.concat({
      id: groupClauseReduxId,
      parentId: null,
      operator: _constants.groupOperators.BOOLEAN,
      value: _constants.groupOperatorValues.AND
    });
  }

  if (!parentId || !clauses.find(function (it) {
    return it.id === parentId;
  })) {
    /**
     * whoops, the dispatcher didn't tell us which groupClause to attach this new clause
     * to.  to figure out our parent, we'll need to...
     *
     * - see if we have any siblings
     *   + if so, find out who their oldest ancestor is
     *   + if not, create a new group to adopt us
     * - add ourselves to the parent we found or created
     */

    // siblings must have conditionNames (group clauses are not considered siblings)

    var siblingClauseTree = [];

    var isAudienceClause = Object.values(_constants.propertyConditionNames).includes(clause.conditionName);

    if (isAudienceClause) {
      if (clause.property !== "_ABGroup") {
        //@todo this needs a test or a better approach
        siblingClauseTree = getFilteredClauseTree(_filters.filterAudienceTargetingClauses, clauses);
      }
    } else {
      siblingClauseTree = getFilteredClauseTree((0, _filters.filterSiblingClauses)(clause.conditionName), clauses);
    }

    if (!groupByConditionName) {
      var rootClause = (0, _utils.getMasterClause)(clauses);
      var clauseReduxId = (0, _utils.getClauseReduxId)(clauses, rootClause.id);

      clauses = clauses.concat(_extends({}, clause, {
        id: clauseReduxId,
        parentId: rootClause.id
      }));
    } else if (siblingClauseTree.length > 0) {
      // there already exists a group where clauses like this one live.  add to it!

      var parentClause = siblingClauseTree.find(function (it) {
        return !siblingClauseTree.find(function (sibling) {
          return it.parentId === sibling.id;
        });
      });

      var _clauseReduxId = (0, _utils.getClauseReduxId)(clauses, parentClause.id);

      clauses = clauses.concat(_extends({}, clause, {
        id: _clauseReduxId,
        parentId: parentClause.id
      }));
    } else {
      // there is no group where clauses like this one live.  create one and add attach it to the root

      var _rootClause = (0, _utils.getMasterClause)(clauses);
      var groupOperator = isAudienceClause ? _constants.groupOperatorValues.AND : _constants.groupOperatorValues.OR;

      clauses = addGroupClause(clauses, _rootClause.id, groupOperator, [clause]);
    }
  } else {
    var _clauseReduxId2 = (0, _utils.getClauseReduxId)(clauses, parentId);

    clauses = clauses.concat(_extends({}, clause, {
      id: _clauseReduxId2,
      parentId: parentId
    }));
  }

  return clauses;
};

/**
 * Given a filter function and full set of flattened clauses, returns a set of clauses
 * and and parents all the way up the tree.  For example...
 *
 * conditionNames: ['url']
 * clauses: [
 *   { id: 1, operator: "boolean", value: "or" },
 *   { id: "1-1", parentId: 1, conditionName: "url", operator: "regex", value: "\/\w+\/foo" }
 *   { id: "1-2", parentId: 1, conditionName: "properties", property: "language" operator: "==", value: "petelish" }
 * ]
 *
 * ...returns...
 *
 * [
 *   { id: 1, operator: "boolean", value: "or" },
 *   { id: "1-1", parentId: 1, conditionName: "url", operator: "regex", value: "\/\w+\/foo" }
 * ]
 *
 * @param filter
 * @param clauses
 * @returns {*}
 */

var getFilteredClauseTree = exports.getFilteredClauseTree = function getFilteredClauseTree(filter, clauses) {
  clauses = clauses ? clauses : [];
  return reduceClauseBranches(clauses.filter(filter).reduce(function (prev, it) {
    return [].concat(_toConsumableArray(prev), [getClauseBranch(it, clauses).reverse()]);
  }, []));
};

/**
 * Each filtered clause tree must consist of the minimum set of nodes such that
 * all descendants branch off of a single root node.  reduceClauseBranches
 * helps ensure this by recursively stripping levels from the root up
 * until a node is found from which all others branch.
 *
 * @param branches
 * @returns {*}
 */

var reduceClauseBranches = exports.reduceClauseBranches = function reduceClauseBranches(branches) {
  // the only elements in a "branch" that could possibly be equal are the boolean/group
  // clauses that tie it together.  for this reason, the below is effectively comparing
  // just boolean clauses

  if (branches.length > 1) {
    if (branches.every(function (branch) {
      return branch[0] === (branches[0] || [])[0];
    })) {
      if (branches.every(function (branch) {
        return branch[1] === (branches[0] || [])[1];
      })) {
        return reduceClauseBranches(branches.map(function (branch) {
          return branch.slice(1);
        }));
      }
    }
  }

  if (branches.length === 1) {
    // since there is only one branch, we must use a different strategy here
    // and simply ensure to stunt the branch at the first boolean clause.

    var branch = branches[0] || [];

    if ((branch[0] || {}).operator === _constants.groupOperators.BOOLEAN && (branch[1] || {}).operator === _constants.groupOperators.BOOLEAN) {
      return reduceClauseBranches([branch.slice(1)]);
    }
  }

  return (0, _index.deduplicateBy)("id", branches.reduce(function (prev, it) {
    return [].concat(_toConsumableArray(prev), _toConsumableArray(it));
  }, []));
};

/**
 * Returns a clause (leaf) node and all of its parents
 *
 * @param clause
 * @param clauses
 * @returns {[*]}
 */

var getClauseBranch = exports.getClauseBranch = function getClauseBranch(clause, clauses) {
  if (!clause) {
    return [];
  }

  var next = [clause];

  if (clause.parentId !== null) {
    next = [].concat(_toConsumableArray(next), _toConsumableArray(getClauseBranch(clauses.find(function (it) {
      return it.id === clause.parentId;
    }), clauses)));
  }

  return next;
};

/**
 * Given an initial array of children and a parent node, creates a new
 * branch with those children attached.
 *
 * @param clauses
 * @param parentId
 * @param boolean
 * @param childClauses
 * @param groupByConditionName
 */

var addGroupClause = exports.addGroupClause = function addGroupClause(clauses, parentId, boolean) {
  var childClauses = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];

  if (childClauses.length > 0) {
    clauses = addClause(clauses, parentId, {
      operator: _constants.groupOperators.BOOLEAN,
      value: boolean
    });

    // we're relying on the fact that new clauses are always added to the end of the clause array.
    var groupClause = clauses[clauses.length - 1];

    childClauses.forEach(function (childClause) {
      return clauses = addClause(clauses, groupClause.id, childClause);
    });
  }

  return clauses;
};

/**
 * Removes the specified clause, then walks up the tree to delete
 * any newly orphaned group clauses.
 *
 * @param clauses
 * @param clauseId
 * @returns {*}
 */

var deleteClause = exports.deleteClause = function deleteClause(clauses, clauseId) {
  var outgoingClause = (clauses || []).find(function (_ref2) {
    var id = _ref2.id;
    return id === clauseId;
  });

  if (outgoingClause) {
    var outgoingClauseIndex = clauses.indexOf(outgoingClause);

    clauses = [].concat(_toConsumableArray(clauses.slice(0, outgoingClauseIndex)), _toConsumableArray(clauses.slice(outgoingClauseIndex + 1)));

    if (outgoingClause.parentId && !clauses.filter(function (_ref3) {
      var parentId = _ref3.parentId;
      return parentId === outgoingClause.parentId;
    }).length) {
      // if this was the only child of a group clause, add a default child clause
      clauses = deleteClause(clauses, outgoingClause.parentId);
    }

    if (outgoingClause.operator === _constants.groupOperators.BOOLEAN) {
      // if a group clause is deleted, remove all of its children as well
      var groupChildren = clauses.filter(function (_ref4) {
        var parentId = _ref4.parentId;
        return parentId === outgoingClause.id;
      });
      groupChildren.forEach(function (_ref5) {
        var id = _ref5.id;
        return clauses = deleteClause(clauses, id);
      });
    }
  }

  return clauses;
};

/**
 * Quickly deletes leaf clauses (and their parents, should they become orphaned)
 * that match a functional "pattern"
 *
 * Useful for deleting all `url` leafs, for example.
 *
 * @param clauses
 * @param filter
 */

var deleteMatchingClauses = exports.deleteMatchingClauses = function deleteMatchingClauses(clauses, filter) {
  var leafClauses = clauses.filter(filter);
  leafClauses.forEach(function (_ref6) {
    var id = _ref6.id;
    return clauses = deleteClause(clauses, id);
  });

  return clauses;
};